<template>
  <v-card :loading="isLoading" flat class="payment-monitoring-page">
    <v-card-title class="payment-monitoring-page-header align-start flex-wrap">
      <div>
        <h1 class="payment-monitoring-page-title">Ödeme Sağlayıcıları</h1>
        <div class="payment-monitoring-page-subtitle">
          Sağlayıcı bazında işlem hacmini, son işlemleri ve operasyonel riskleri
          karşılaştırın.
        </div>
      </div>
      <v-spacer />
      <v-btn small outlined color="primary" @click="loadProviders">
        <v-icon small left>mdi-refresh</v-icon>
        Yenile
      </v-btn>
    </v-card-title>
    <v-card-text class="pa-0">
      <div class="payment-monitoring-context-row">
        <span class="payment-monitoring-context-chip">
          Seçilen tarih aralığı: <strong>{{ periodLabel }}</strong>
        </span>
        <span class="payment-monitoring-context-chip">
          Sağlayıcı sayısı: <strong>{{ providerCount }}</strong>
        </span>
        <span class="payment-monitoring-context-chip">
          Yeni ödemeye açık: <strong>{{ enabledProviderCount }}</strong>
        </span>
      </div>

      <div class="payment-monitoring-callout provider-monitoring-callout">
        <v-icon small color="info">mdi-pulse</v-icon>
        <span>
          “Çalışıyor” durumu kesintisiz hizmet garantisi değildir. Yeni ödeme
          durumu, son başarılı işlem ve sonuçlanmamış kayıtlar birlikte
          değerlendirilir.
        </span>
      </div>

      <payment-monitoring-filters
        v-model="filters"
        :show-query="false"
        @apply="applyFilters"
      />

      <v-row dense class="provider-metrics-row">
        <v-col
          v-for="metric in metrics"
          :key="metric.label"
          cols="12"
          sm="6"
          md="3"
        >
          <payment-monitoring-metric v-bind="metric" />
        </v-col>
      </v-row>

      <v-card outlined class="payment-monitoring-panel mb-5">
        <v-card-title class="payment-monitoring-panel-header">
          <div>
            <div class="payment-monitoring-section-title">
              Sağlayıcı karşılaştırması
            </div>
            <div class="payment-monitoring-section-description">
              Seçilen dönemdeki ödeme kayıtları ve son işlem bilgileri.
            </div>
          </div>
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="response.items || []"
          :loading="isLoading"
          hide-default-footer
          disable-pagination
        >
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="30" color="primary" class="mr-2" dark>
                <span class="white--text text-caption font-weight-bold">{{
                  providerInitials(item)
                }}</span>
              </v-avatar>
              <div>
                <strong>{{ item.name }}</strong>
                <div class="caption text--secondary">{{ item.code }}</div>
              </div>
            </div>
          </template>
          <template v-slot:item.new_payments_enabled="{ value }">
            <v-chip x-small dark :color="value ? 'success' : 'grey'">
              {{ value ? "Açık" : "Kapalı" }}
            </v-chip>
          </template>
          <template v-slot:item.commissions_stale="{ item }">
            <div class="provider-rates-summary">
              <div class="provider-rates-primary">
                <strong class="provider-rates-value">{{
                  ratePercent(item.provider_cost_rate_percent)
                }}</strong>
                <span>Komisyon oranı</span>
              </div>
              <div class="provider-rates-actions">
                <button
                  type="button"
                  class="provider-rates-link"
                  @click.stop="openDetail(item)"
                >
                  <span>Taksit oranları</span>
                  <span
                    class="provider-rates-state"
                    :class="`provider-rates-state--${
                      commissionSyncMeta(item).color
                    }`"
                  >
                    <span class="provider-rates-state-dot" />
                    {{ commissionSyncMeta(item).text }}
                  </span>
                </button>
                <v-tooltip v-if="item.commission_sync_supported" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      icon
                      x-small
                      color="primary"
                      :loading="commissionSyncProviderId === item.id"
                      :disabled="commissionSyncProviderId !== null"
                      @click.stop="syncProviderCommissions(item)"
                    >
                      <v-icon small>mdi-sync</v-icon>
                    </v-btn>
                  </template>
                  <span>Taksit oranlarını güncelle</span>
                </v-tooltip>
              </div>
              <div class="provider-rates-date">
                Son kontrol · {{ formatRateDate(item.commissions_synced_at) }}
              </div>
            </div>
          </template>
          <template v-slot:item.success_rate="{ value }">{{
            percent(value)
          }}</template>
          <template v-slot:item.volume_share="{ value }">
            <div class="provider-volume-cell">
              <span class="provider-volume-track">
                <span
                  class="provider-volume-fill"
                  :style="{ width: `${Math.min(Number(value) || 0, 100)}%` }"
                />
              </span>
              <strong>{{ percent(value) }}</strong>
            </div>
          </template>
          <template v-slot:item.provider_cost_amount="{ value }">
            <rs-table-cell-number price :value="value" />
          </template>
          <template v-slot:item.attention_count="{ value }">
            <v-chip x-small dark :color="value ? 'warning' : 'success'">{{
              value
            }}</v-chip>
          </template>
          <template v-slot:item.last_success_at="{ value }">{{
            formatDate(value)
          }}</template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openDetail(item)"
              ><v-icon small>mdi-chevron-right</v-icon></v-btn
            >
          </template>
        </v-data-table>
      </v-card>

      <v-card outlined class="payment-monitoring-panel">
        <v-card-title class="payment-monitoring-panel-header">
          <div>
            <div class="payment-monitoring-section-title">
              Sağlayıcı bazında incelenecek kayıtlar
            </div>
            <div class="payment-monitoring-section-description">
              Sonuçlanmamış veya manuel inceleme gerektiren kayıtların sağlayıcı
              dağılımı.
            </div>
          </div>
        </v-card-title>
        <v-simple-table>
          <thead>
            <tr>
              <th>Sağlayıcı</th>
              <th class="text-right">Bekleyen</th>
              <th class="text-right">Bilinmeyen</th>
              <th class="text-right">Manuel inceleme</th>
              <th class="text-right">Toplam incelenecek</th>
              <th>Son başarı</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="provider in response.items || []" :key="provider.id">
              <td>
                <strong>{{ provider.name }}</strong>
              </td>
              <td class="text-right">{{ provider.pending_count }}</td>
              <td class="text-right">{{ provider.unknown_count }}</td>
              <td class="text-right">{{ provider.manual_review_count }}</td>
              <td class="text-right">{{ provider.attention_count }}</td>
              <td>{{ formatDate(provider.last_success_at) }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
    </v-card-text>

    <v-navigation-drawer
      v-model="detailOpen"
      temporary
      fixed
      right
      width="520"
      class="payment-monitoring-drawer"
    >
      <v-card v-if="selectedProvider" flat class="provider-detail-card">
        <v-card-title class="provider-detail-header">
          <v-avatar size="38" color="primary" dark class="mr-3">
            <span class="text-caption font-weight-bold">{{
              providerInitials(selectedProvider)
            }}</span>
          </v-avatar>
          <div class="provider-detail-header-copy">
            <div class="provider-detail-title">{{ selectedProvider.name }}</div>
            <div class="provider-detail-header-meta">
              <span>{{ selectedProvider.code }}</span>
              <span
                class="provider-detail-status"
                :class="
                  selectedProvider.new_payments_enabled
                    ? 'provider-detail-status--success'
                    : 'provider-detail-status--warning'
                "
              >
                <span class="provider-detail-status-dot" />
                {{
                  selectedProvider.new_payments_enabled
                    ? "Yeni ödemeye açık"
                    : "Yeni ödemeye kapalı"
                }}
              </span>
            </div>
          </div>
          <v-spacer />
          <v-btn icon aria-label="Detayı kapat" @click="detailOpen = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-divider />
        <v-card-text class="provider-detail-body">
          <div
            v-if="!selectedProvider.new_payments_enabled"
            class="provider-detail-notice"
          >
            <v-icon small color="warning">mdi-alert-outline</v-icon>
            <span>
              Yeni ödeme alınmıyor; mevcut ödeme sonuçları izlenmeye devam
              ediyor.
            </span>
          </div>

          <section class="provider-detail-section">
            <div class="provider-detail-section-heading">
              <div>
                <div class="provider-detail-section-title">Oranlar</div>
                <div class="provider-detail-section-description">
                  Sağlayıcı maliyeti, non-3DS limiti ve aktif taksit oranları
                </div>
              </div>
              <v-btn
                v-if="selectedProvider.commission_sync_supported"
                text
                small
                color="primary"
                :loading="commissionSyncProviderId === selectedProvider.id"
                :disabled="commissionSyncProviderId !== null"
                @click="syncProviderCommissions(selectedProvider)"
              >
                <v-icon left small>mdi-sync</v-icon>
                Taksit oranlarını güncelle
              </v-btn>
            </div>

            <div class="provider-rates-overview">
              <div class="provider-current-rate">
                <span>Tek çekim komisyonu</span>
                <strong>{{
                  ratePercent(selectedProvider.provider_cost_rate_percent)
                }}</strong>
              </div>
              <div class="provider-current-rate provider-current-rate--limit">
                <span>Non-3DS işlem limiti</span>
                <strong v-if="selectedProvider.non_3ds_max_amount !== null">
                  <rs-table-cell-number
                    price
                    :value="selectedProvider.non_3ds_max_amount"
                  />
                </strong>
                <strong v-else>-</strong>
              </div>
              <div class="provider-rates-status">
                <span
                  class="provider-rates-state"
                  :class="`provider-rates-state--${
                    commissionSyncMeta(selectedProvider).color
                  }`"
                >
                  <span class="provider-rates-state-dot" />
                  Taksit oranları
                  {{ commissionSyncMeta(selectedProvider).text }}
                </span>
                <span>Son kontrol</span>
                <strong>{{
                  formatRateDate(selectedProvider.commissions_synced_at)
                }}</strong>
              </div>
            </div>

            <div
              v-if="installmentRates(selectedProvider).length"
              class="provider-installment-grid"
            >
              <div
                v-for="rate in installmentRates(selectedProvider)"
                :key="rate.installments"
                class="provider-installment-rate"
              >
                <span>{{ rate.installments }} taksit</span>
                <strong>{{ ratePercent(rate.rate_percent) }}</strong>
              </div>
            </div>
            <div v-else class="provider-detail-empty">
              Aktif taksit oranı bulunmuyor.
            </div>
          </section>

          <section class="provider-detail-section">
            <div class="provider-detail-section-heading">
              <div>
                <div class="provider-detail-section-title">
                  Sağlayıcı detayları
                </div>
                <div class="provider-detail-section-description">
                  Seçili dönemin operasyonel özeti
                </div>
              </div>
            </div>
            <div class="provider-detail-list">
              <div class="provider-detail-row">
                <span>Aktif TYA</span>
                <strong>{{ selectedProvider.active_cluster_count }}</strong>
              </div>
              <div class="provider-detail-row">
                <span>Seçili TYA</span>
                <strong>{{ selectedProvider.selected_cluster_count }}</strong>
              </div>
              <div class="provider-detail-row">
                <span>İşlem denemesi</span>
                <strong>{{ selectedProvider.attempt_count }}</strong>
              </div>
              <div class="provider-detail-row">
                <span>Başarı oranı</span>
                <strong>{{ percent(selectedProvider.success_rate) }}</strong>
              </div>
              <div class="provider-detail-row">
                <span>Karttan çekilen</span>
                <strong>
                  <rs-table-cell-number
                    price
                    :value="selectedProvider.charged_amount"
                  />
                </strong>
              </div>
              <div class="provider-detail-row">
                <span>Sağlayıcı maliyeti</span>
                <strong>
                  <rs-table-cell-number
                    price
                    :value="selectedProvider.provider_cost_amount"
                  />
                </strong>
              </div>
              <div class="provider-detail-row">
                <span>İncelenecek kayıt</span>
                <strong>{{ selectedProvider.attention_count }}</strong>
              </div>
              <div class="provider-detail-row">
                <span>Son başarılı ödeme</span>
                <strong>{{
                  formatDate(selectedProvider.last_success_at)
                }}</strong>
              </div>
              <div class="provider-detail-row provider-detail-row--top">
                <span>Hakediş teyit kaynağı</span>
                <strong>Ortak teyit kaynağı bulunmuyor</strong>
              </div>
            </div>
          </section>
        </v-card-text>
      </v-card>
    </v-navigation-drawer>
  </v-card>
</template>

<script>
import { hasPermissions, isPage } from "@/view/mixins";
import PaymentMonitoringFilters from "./components/PaymentMonitoringFilters.vue";
import PaymentMonitoringMetric from "./components/PaymentMonitoringMetric.vue";
import {
  buildPaymentMonitoringParams,
  formatMonitoringDate,
  formatMonitoringPercent,
  paymentMonitoringFiltersFromQuery,
  syncPaymentMonitoringUrl,
} from "./paymentMonitoring";

const emptyResponse = () => ({ meta: { period: {} }, summary: {}, items: [] });

export default {
  name: "PaymentProviders",
  components: { PaymentMonitoringFilters, PaymentMonitoringMetric },
  mixins: [hasPermissions, isPage],
  data() {
    return {
      pageMeta: { title: "Ödeme Sağlayıcıları" },
      isLoading: false,
      detailOpen: false,
      selectedProvider: null,
      commissionSyncProviderId: null,
      filters: paymentMonitoringFiltersFromQuery(this.$route.query),
      response: emptyResponse(),
      headers: [
        { text: "Sağlayıcı", value: "name" },
        { text: "Yeni ödeme", value: "new_payments_enabled", align: "center" },
        {
          text: "Oranlar",
          value: "commissions_stale",
          sortable: false,
          width: "210px",
        },
        { text: "Aktif TYA", value: "active_cluster_count", align: "end" },
        { text: "İşlem", value: "attempt_count", align: "end" },
        { text: "Başarı", value: "success_rate", align: "end" },
        { text: "Hacim payı", value: "volume_share", align: "end" },
        {
          text: "Sağlayıcı maliyeti",
          value: "provider_cost_amount",
          align: "end",
        },
        { text: "İncelenecek", value: "attention_count", align: "center" },
        { text: "Son başarı", value: "last_success_at" },
        { text: "", value: "actions", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    metrics() {
      const value = this.response.summary || {};
      return [
        {
          label: "Toplam işlem",
          value: value.attempt_count,
          caption: "Tüm sağlayıcı denemeleri",
          icon: "mdi-format-list-numbered",
          color: "primary",
          footLabel: "Başarılı deneme",
          footValue: value.successful_attempt_count || 0,
        },
        {
          label: "Genel başarı oranı",
          value: formatMonitoringPercent(value.success_rate),
          caption: `${value.successful_attempt_count || 0} başarılı deneme`,
          icon: "mdi-check-circle-outline",
          color: "success",
          footLabel: "Sağlayıcı sayısı",
          footValue: this.providerCount,
        },
        {
          label: "Aktif TYA",
          value: value.active_cluster_count,
          caption: "Ödeme sağlayıcısı etkin",
          icon: "mdi-office-building-outline",
          color: "info",
          footLabel: "Yeni ödemeye açık",
          footValue: this.enabledProviderCount,
        },
        {
          label: "İncelenecek kayıt",
          value: value.attention_count,
          caption: "Bekleyen, bilinmeyen ve manuel",
          icon: "mdi-alert-outline",
          color: "warning",
          footLabel: "Kayıt kaynağı",
          footValue: "Robosay",
        },
      ];
    },
    providerCount() {
      return (this.response.items || []).length;
    },
    enabledProviderCount() {
      return (this.response.items || []).filter(
        (provider) => provider.new_payments_enabled
      ).length;
    },
    periodLabel() {
      const period = this.response.meta?.period || {};
      return period.min && period.max ? `${period.min} - ${period.max}` : "-";
    },
  },
  mounted() {
    this.syncUrlState();
    this.loadProviders();
  },
  methods: {
    applyFilters() {
      this.syncUrlState();
      this.loadProviders();
    },
    syncUrlState() {
      syncPaymentMonitoringUrl(this.filters);
    },
    loadProviders() {
      if (this.isLoading) return;
      this.isLoading = true;
      return this.$api
        .query("api-super-admin/payment-monitoring/providers", {
          params: buildPaymentMonitoringParams(this.filters),
        })
        .then((response) => {
          this.response = response.data.data || emptyResponse();

          if (this.selectedProvider) {
            this.selectedProvider = (this.response.items || []).find(
              (provider) => provider.id === this.selectedProvider.id
            );
          }
        })
        .catch((error) => this.handleError(error))
        .finally(() => (this.isLoading = false));
    },
    openDetail(provider) {
      this.selectedProvider = provider;
      this.detailOpen = true;
    },
    providerInitials(provider) {
      return (provider.code || provider.name || "PP").slice(0, 2).toUpperCase();
    },
    commissionSyncMeta(provider) {
      if (!provider.commission_sync_supported) {
        return {
          color: "grey",
          text: "Destek yok",
        };
      }

      if (!provider.commissions_synced_at) {
        return {
          color: "warning",
          text: "Henüz yok",
        };
      }

      if (provider.commissions_stale) {
        return {
          color: "warning",
          text: "Güncel değil",
        };
      }

      return {
        color: "success",
        text: "Güncel",
      };
    },
    installmentRates(provider) {
      return (provider.installment_commission_rates || []).filter(
        (rate) => Number(rate.installments) > 1
      );
    },
    ratePercent(value) {
      if (value === null || value === undefined || value === "") return "-";

      return `%${Number(value).toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    formatRateDate(value) {
      if (!value) return "-";

      return new Intl.DateTimeFormat("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value));
    },
    syncProviderCommissions(provider) {
      provider = provider || this.selectedProvider;

      if (!provider || !provider.commission_sync_supported) return;
      if (
        !window.confirm(`${provider.name} komisyonları şimdi güncellensin mi?`)
      ) {
        return;
      }

      this.commissionSyncProviderId = provider.id;
      this.$api
        .post(
          `api-super-admin/payment-ops/providers/${provider.id}/commissions/sync`
        )
        .then((response) => {
          const result = response.data.data;
          this.$toast.success(
            result.changed_count
              ? `${result.changed_count} komisyon oranı güncellendi.`
              : "Komisyonlar kontrol edildi; oran değişikliği yok."
          );
          return this.loadProviders();
        })
        .catch((error) => this.handleError(error))
        .finally(() => (this.commissionSyncProviderId = null));
    },
    percent: formatMonitoringPercent,
    formatDate: formatMonitoringDate,
  },
};
</script>

<style lang="scss">
@import "./payment-monitoring.scss";
</style>

<style scoped>
.provider-monitoring-callout {
  margin-top: 0;
  margin-bottom: 12px;
}

.provider-metrics-row {
  margin-bottom: 18px;
}

.provider-rates-summary {
  min-width: 175px;
  padding: 5px 0;
  white-space: nowrap;
}

.provider-rates-primary,
.provider-rates-actions,
.provider-rates-link,
.provider-rates-status,
.provider-current-rate {
  display: flex;
  align-items: center;
}

.provider-rates-primary {
  gap: 7px;
  color: #7e8299;
  font-size: 11px;
}

.provider-rates-value {
  color: #3f4254;
  font-size: 14px;
  line-height: 1.2;
}

.provider-rates-actions {
  min-height: 27px;
  gap: 2px;
}

.provider-rates-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #5e6278;
  font-size: 12px;
  font-weight: 600;
  gap: 8px;
  cursor: pointer;
}

.provider-rates-link:hover span:first-child {
  color: #5867dd;
}

.provider-rates-link:focus-visible {
  border-radius: 3px;
  outline: 2px solid rgba(88, 103, 221, 0.35);
  outline-offset: 2px;
}

.provider-rates-state {
  display: inline-flex;
  align-items: center;
  color: #7e8299;
  font-size: 11px;
  font-weight: 600;
  gap: 4px;
}

.provider-rates-state-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.provider-rates-state--success {
  color: #0f9f76;
}

.provider-rates-state--warning {
  color: #c78200;
}

.provider-rates-date {
  color: #7e8299;
  font-size: 10px;
  line-height: 1.2;
}

.provider-detail-card {
  display: flex;
  min-height: 100%;
  height: 100%;
  flex-direction: column;
  overflow: hidden;
}

.provider-detail-header {
  flex: 0 0 auto;
  gap: 0;
  padding: 18px 20px !important;
  background: #ffffff;
}

.provider-detail-header-copy {
  min-width: 0;
}

.provider-detail-title {
  color: #181c32;
  font-size: 19px;
  font-weight: 800;
  line-height: 1.25;
}

.provider-detail-header-meta,
.provider-detail-status {
  display: flex;
  align-items: center;
}

.provider-detail-header-meta {
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 5px;
  color: #7e8299;
  font-size: 12px;
  font-weight: 600;
}

.provider-detail-status {
  gap: 5px;
}

.provider-detail-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.provider-detail-status--success {
  color: #0f9f76;
}

.provider-detail-status--warning {
  color: #c78200;
}

.provider-detail-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 20px !important;
}

.provider-detail-notice {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-bottom: 20px;
  padding: 11px 13px;
  border: 1px solid rgba(255, 168, 0, 0.25);
  border-radius: 5px;
  background: rgba(255, 168, 0, 0.07);
  color: #5e6278;
  font-size: 12px;
  line-height: 1.5;
}

.provider-detail-section + .provider-detail-section {
  margin-top: 24px;
}

.provider-detail-section-heading {
  display: flex;
  min-height: 35px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 11px;
}

.provider-detail-section-title {
  color: #3f4254;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.025em;
  line-height: 1.4;
  text-transform: uppercase;
}

.provider-detail-section-description {
  margin-top: 2px;
  color: #a1a5b7;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.4;
}

.provider-rates-overview {
  display: grid;
  overflow: hidden;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border: 1px solid #e4e6ef;
  border-radius: 5px;
  background: #fafbfc;
}

.provider-current-rate {
  align-items: flex-start;
  justify-content: center;
  padding: 15px;
  color: #7e8299;
  flex-direction: column;
  font-size: 11px;
  font-weight: 600;
}

.provider-current-rate strong {
  color: #3f4254;
  font-size: 23px;
  line-height: 1.25;
}

.provider-current-rate--limit {
  border-left: 1px solid #e4e6ef;
}

.provider-current-rate--limit strong {
  font-size: 18px;
}

.provider-rates-status {
  align-items: flex-start;
  justify-content: center;
  padding: 13px 15px;
  border-left: 1px solid #e4e6ef;
  color: #7e8299;
  font-size: 11px;
  line-height: 1.45;
  flex-direction: column;
}

.provider-rates-status > span:nth-child(2) {
  margin-top: 5px;
  color: #a1a5b7;
  font-size: 10px;
}

.provider-rates-status > strong {
  color: #5e6278;
  font-size: 11px;
  font-weight: 700;
}

.provider-installment-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 7px;
  margin-top: 9px;
}

.provider-installment-rate {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 10px;
  border: 1px solid #e4e6ef;
  border-radius: 4px;
  background: #ffffff;
}

.provider-installment-rate span {
  color: #7e8299;
  font-size: 10px;
  font-weight: 600;
  text-transform: capitalize;
}

.provider-installment-rate strong {
  color: #3f4254;
  font-size: 12px;
  font-weight: 800;
}

.provider-detail-empty {
  margin-top: 9px;
  padding: 13px;
  border: 1px dashed #dfe2ea;
  border-radius: 4px;
  color: #a1a5b7;
  font-size: 11px;
  text-align: center;
}

.provider-detail-list {
  overflow: hidden;
  border: 1px solid #e4e6ef;
  border-radius: 5px;
  background: #ffffff;
}

.provider-detail-row {
  display: flex;
  min-height: 43px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 9px 13px;
  border-bottom: 1px solid #eff1f5;
}

.provider-detail-row:last-child {
  border-bottom: 0;
}

.provider-detail-row--top {
  align-items: flex-start;
}

.provider-detail-row > span {
  flex: 0 0 42%;
  color: #7e8299;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
}

.provider-detail-row > strong {
  min-width: 0;
  color: #3f4254;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
  text-align: right;
  word-break: break-word;
}

.provider-volume-cell {
  display: flex;
  min-width: 112px;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
}

.provider-volume-track {
  display: inline-flex;
  width: 58px;
  height: 5px;
  overflow: hidden;
  border-radius: 999px;
  background: #eceef5;
}

.provider-volume-fill {
  min-width: 0;
  border-radius: inherit;
  background: #5867dd;
}

.provider-volume-cell strong {
  min-width: 42px;
  color: #3f4254;
  font-size: 11px;
}

.payment-monitoring-panel ::v-deep .v-data-table,
.payment-monitoring-panel ::v-deep .v-data-table__wrapper,
.payment-monitoring-panel ::v-deep .v-data-table__wrapper table {
  border-radius: 0;
}

@media (max-width: 600px) {
  .provider-detail-section-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .provider-rates-overview {
    grid-template-columns: 1fr;
  }

  .provider-rates-status {
    border-top: 1px solid #e4e6ef;
    border-left: 0;
  }

  .provider-current-rate--limit {
    border-top: 1px solid #e4e6ef;
    border-left: 0;
  }

  .provider-installment-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
